import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { getFirstNonEmpty } from "../utils/common";
import { addMoney, resolveMoneyDecimals, roundMoney, sumMoney } from "../utils/money";
import { markForSync } from "./syncService";
import { getAllInvoices } from "./invoiceService";
import formaDePagoService from "./formaDePagoService";
import { getDocumentTypeSign } from "../constants/documentType";
import dayjs from "dayjs";
import { crearRegistro } from "../utils/registro";

// El arqueo del cierre se reconstruye desde acá, y es de las cosas que sólo
// se pueden diagnosticar sobre el terminal donde no cuadró: qué rango se
// buscó, cuántas facturas cayeron dentro, cuántas líneas de pago y qué
// total. Esas cuatro líneas se conservan; el resto era narración.
const registro = crearRegistro("pagos");

/**
 * El arqueo cuenta dinero que se movió, y una forma de pago deducida no movió
 * ninguno.
 *
 * Se crean para que una nota de crédito sobre una factura histórica sin detalle
 * de pago pueda cumplir la regla de «pendiente 0» --Costa Rica tiene 30 filas de
 * `DetallePagoFactura` para 140.837 facturas de caja--. Contarlas en el arqueo
 * lo descuadraría por un importe que nadie entregó ni recibió.
 *
 * Acuerdo `contrato-nota-de-credito-sobre-historicos`, ronda 3.
 */
const sinLasDeducidas = (line) => !line?.deducida;

const resolveInvoiceForCierreLine = (line, invoicesInRange) => {
  if (line.invoiceId != null && line.invoiceId !== "") {
    const byId = invoicesInRange.find((inv) => inv.id === line.invoiceId);
    if (byId) return byId;
  }
  const ref = line.invoiceNo;
  if (ref != null && ref !== "" && ref !== "Desconocido") {
    const byRef = invoicesInRange.find(
      (inv) => inv.No === ref || inv.Pre_Assigned_No === ref || String(inv.id) === String(ref)
    );
    if (byRef) return byRef;
  }
  return undefined;
};

// Funciones para gestionar pagos de facturas

export const addInvoicePaymentLine = async (paymentLine, country = null) => {
  try {
    const decimals = resolveMoneyDecimals(country);
    // El Dto_Amount viene ya calculado desde el formulario
    const discountPercentage = paymentLine.Dto || 0;
    const amount = roundMoney(paymentLine.Amount || 0, decimals);
    const discountAmount = roundMoney(paymentLine.Dto_Amount || 0, decimals);

    // Crear una línea de pago en la base de datos local
    const id = await db.invoicePaymentLines.add({
      invoiceId: paymentLine.invoiceId,
      Document_Type: paymentLine.Document_Type || "Invoice",
      Document_No_: paymentLine.Document_No_,
      Line_No: paymentLine.Line_No,
      Payment_Method: paymentLine.Payment_Method,
      Amount: amount,
      Dto: discountPercentage,
      Dto_Amount: discountAmount,
      pendingSync: 1,
      lastModified: new Date(),
      deleted: false,
    });

    return {
      ...paymentLine,
      id,
      Amount: amount,
      Dto_Amount: discountAmount,
    };
  } catch (error) {
    registro.error("Error al crear línea de pago:", error);
    throw error;
  }
};

/**
 * Si el pago dice a qué factura contabilizada pertenece, tiene que ser ésta.
 *
 * Los pagos traídos por Traer Históricos se guardan bajo el número de borrador
 * (`Document_No_`), y ese número lo pueden compartir dos facturas contabilizadas
 * -- ver el comentario de `Documento_Definitivo` en utils/syncUtils.js. La
 * importación ya descarta los pagos que no son de ningún documento del lote;
 * acá se resuelve el caso que aquélla no puede: dos hermanas traídas juntas,
 * las dos buscando por el mismo número.
 *
 * Sólo descarta cuando puede probar que el pago es de otra: un pago local o de
 * una factura aún sin contabilizar no trae el campo, y un documento sin `No`
 * (borrador) no tiene contra qué comparar.
 */
/**
 * Se exporta para que quien lea los pagos en bloque --el listado, que no puede
 * hacer una consulta por fila-- aplique el mismo criterio que la ficha. Dos
 * formas de decidir de qué documento es un pago acabarían dando dos respuestas.
 */
export const esPagoDeEsteDocumento = (linea, invoice) => {
  const definitivo = linea?.Documento_Definitivo;
  if (!definitivo || !invoice?.No) return true;
  return definitivo === invoice.No;
};

export const getInvoicePaymentLines = async (invoice) => {
  try {
    if (!invoice) {
      registro.warn("No se proporcionó un documento válido");
      return [];
    }

    let result = [];
    let foundByDocumentNo = false;

    // 1. Priorizar búsqueda por número de documento cuando exista
    const documentNoToUse = getFirstNonEmpty(invoice.Pre_Assigned_No, invoice.No);

    if (documentNoToUse) {
      const byDocNo = (
        await db.invoicePaymentLines
          .where("Document_No_")
          .equals(documentNoToUse)
          .and((line) => !line.deleted)
          .toArray()
      ).filter((line) => esPagoDeEsteDocumento(line, invoice));

      foundByDocumentNo = byDocNo.length > 0;
      result = result.concat(byDocNo);
    }

    // 2. Fallback por ID local (Dexie):
    if (invoice.id && (!documentNoToUse || !foundByDocumentNo)) {
      // El mismo filtro que arriba, o este respaldo volvería a meter el pago
      // que se acaba de descartar: al quitarlo, `foundByDocumentNo` queda en
      // falso y esta rama se activa.
      const byId = (
        await db.invoicePaymentLines
          .where("invoiceId")
          .equals(invoice.id)
          .and((line) => !line.deleted)
          .toArray()
      ).filter((line) => esPagoDeEsteDocumento(line, invoice));

      result = result.concat(byId);
    }

    return Object.values(
      result.reduce((acc, line) => {
        const key =
          line.id ??
          `${line.invoiceId ?? ""}-${line.Document_No_ ?? ""}-${line.Line_No ?? ""}-${line.Payment_Method ?? ""}-${line.Amount ?? ""}`;
        acc[key] = line;
        return acc;
      }, {})
    );
  } catch (error) {
    registro.error(
      `Error al obtener líneas de pago para factura ${invoice?.No || invoice?.Pre_Assigned_No || invoice.id}:`,
      error
    );
    throw error;
  }
};

export const updateInvoicePaymentLine = async (id, updatedLine, country = null) => {
  try {
    // Obtener la línea actual
    const existingLine = await db.invoicePaymentLines.get(id);
    if (!existingLine) {
      throw new Error("La línea de pago no existe");
    }

    const now = getCurrentDateTimeISO();
    const decimals = resolveMoneyDecimals(country);

    // El Dto_Amount viene ya calculado desde el formulario
    const discountAmount = updatedLine.Dto_Amount || 0;

    const lineToUpdate = {
      ...updatedLine,
      Amount: roundMoney(updatedLine.Amount || 0, decimals),
      Dto_Amount: roundMoney(discountAmount, decimals),
      pendingSync: 1,
      lastModified: now,
    };

    await db.invoicePaymentLines.update(id, lineToUpdate);

    // Marcar como pendiente de sincronización
    await markForSync("invoicePaymentLines", id);

    return { ...lineToUpdate, id };
  } catch (error) {
    registro.error(`Error al actualizar línea de pago con ID ${id}:`, error);
    throw error;
  }
};

export const deleteInvoicePaymentLine = async (id) => {
  try {
    // Verificar si la línea existe antes de intentar borrar
    const existingLine = await db.invoicePaymentLines.get(id);

    if (!existingLine) {
      registro.warn(`La línea de pago con ID ${id} no existe o ya fue eliminada.`);
      return false;
    }

    await db.invoicePaymentLines.delete(id);

    registro.log(`Línea de pago ${id} eliminada físicamente de IndexedDB`);
    return true;
  } catch (error) {
    registro.error(`Error al realizar borrado físico de la línea ${id}:`, error);
    throw error;
  }
};

// Obtener todas las líneas de pago de facturas por fecha específica
export const getPaymentLinesByDate = async (date, country = null) => {
  try {
    const decimals = resolveMoneyDecimals(country);
    // Formatear la fecha para comparación
    const targetDate = dayjs(date).format("YYYY-MM-DD");

    // Obtener todas las facturas
    const invoices = await getAllInvoices();

    // Filtrar facturas por fecha - INCLUIR TANTO FACTURAS COMO NOTAS DE CRÉDITO
    const invoicesOnDate = invoices.filter((invoice) => {
      const invoiceDate = dayjs(invoice.Posting_Date).format("YYYY-MM-DD");
      const isValidType = ["Invoice", "Credit Memo", "Boleta"].includes(invoice.Document_Type);
      const isNotDraft = !invoice.isDraft;
      return invoiceDate === targetDate && isValidType && isNotDraft;
    });

    if (invoicesOnDate.length === 0) {
      return [];
    }

    // Obtener los IDs de las facturas
    const invoiceIds = invoicesOnDate.map((invoice) => invoice.id);

    // Obtener todas las líneas de pago que pertenezcan a esas facturas
    const allPaymentLines = await db.invoicePaymentLines
      .filter((line) => !line.deleted && invoiceIds.includes(line.invoiceId))
      .toArray();

    // Obtener todas las formas de pago para enriquecer con el tipo de pago
    const formasDePago = await formaDePagoService.getAll();

    // Enriquecer las líneas de pago
    const enrichedPaymentLines = allPaymentLines.map((line) => {
      const relatedInvoice = invoicesOnDate.find((inv) => inv.id === line.invoiceId);

      const paymentMethod =
        (line.Payment_Method && line.Payment_Method.trim()) ||
        (relatedInvoice?.Payment_Method && relatedInvoice.Payment_Method.trim()) ||
        "EFECTIVO";

      const formaPago = formasDePago.find(
        (fp) => fp.Code.trim().toUpperCase() === paymentMethod.trim().toUpperCase()
      );

      const rawAmount = line.Amount || 0;
      const docSign = getDocumentTypeSign(relatedInvoice?.Document_Type);
      const adjustedAmount = roundMoney(docSign < 0 ? -Math.abs(rawAmount) : rawAmount, decimals);

      // Prioridad para identificar la factura: 1) No (BC), 2) Pre_Assigned_No, 3) ID interno de Dexie.
      return {
        ...line,
        rawAmount,
        Amount: adjustedAmount,
        invoiceNo:
          getFirstNonEmpty(
            relatedInvoice?.No,
            relatedInvoice?.Pre_Assigned_No,
            relatedInvoice?.id,
            line.invoiceId
          ) ?? "Desconocido",
        clientName: relatedInvoice?.Bill_to_Name || "Cliente desconocido",
        documentType: relatedInvoice?.Document_Type || "Unknown",
        paymentMethodCode: formaPago?.Code || line.Payment_Method || "-",
        paymentMethodName: formaPago?.Description || "-",
        paymentMethodType: formaPago?.Tipo_Pago || "Efectivo",
      };
    });

    // AGRUPAR POR FACTURA
    const groupedByInvoice = Object.values(
      enrichedPaymentLines.reduce((acc, line) => {
        const key = `${line.invoiceNo}-${line.paymentMethodCode}`;
        if (!acc[key]) {
          acc[key] = {
            invoiceNo: line.invoiceNo,
            clientName: line.clientName,
            documentType: line.documentType,
            paymentMethodCode: line.paymentMethodCode,
            paymentMethodName: line.paymentMethodName,
            paymentMethodType: line.paymentMethodType,
            Amount: line.Amount,
            lines: [line],
          };
        } else {
          acc[key].Amount = addMoney(acc[key].Amount, line.Amount, decimals);
          acc[key].lines.push(line);
        }
        return acc;
      }, {})
    );
    return groupedByInvoice;
  } catch (error) {
    registro.error(`Error al obtener líneas de pago para la fecha ${date}:`, error);
    throw error;
  }
};

// Obtener todas las líneas de pago de facturas en un rango de fecha y hora específico
// (desde apertura de caja hasta momento del cierre)
export const getPaymentLinesByDateTimeRange = async (
  aperturaDateTime,
  cierreDateTime = null,
  historicos = false,
  country = null
) => {
  try {
    const decimals = resolveMoneyDecimals(country);
    const endDateTime = cierreDateTime || new Date();
    const startDateTime = dayjs(aperturaDateTime);
    const endDateTimeFormatted = dayjs(endDateTime);

    registro.log(
      `Arqueo del rango ${startDateTime.format(
        "YYYY-MM-DD HH:mm:ss"
      )} a ${endDateTimeFormatted.format("YYYY-MM-DD HH:mm:ss")}`
    );

    // Obtener todas las facturas
    const invoices = await getAllInvoices();

    // Filtrar facturas por rango de fecha y hora usando lastModified
    const invoicesInRange = invoices.filter((invoice) => {
      if (!invoice.lastModified) return false;

      const invoiceDateTime = dayjs(invoice.lastModified);

      const isInRange =
        invoiceDateTime.isSame(startDateTime) ||
        invoiceDateTime.isSame(endDateTimeFormatted) ||
        (invoiceDateTime.isAfter(startDateTime) && invoiceDateTime.isBefore(endDateTimeFormatted));

      const isValidType =
        invoice.Document_Type === "Invoice" ||
        invoice.Document_Type === "Credit Memo" ||
        invoice.Document_Type === "Boleta";

      const isNotDraft = !invoice.isDraft;

      return isInRange && isValidType && isNotDraft;
    });

    registro.log(`Facturas en el rango: ${invoicesInRange.length}`);

    if (invoicesInRange.length === 0) {
      return [];
    }

    // Obtener los IDs de las facturas
    const invoiceIds = !historicos
      ? invoicesInRange.map((invoice) => invoice.id)
      : [
          ...new Set(
            invoicesInRange
              .flatMap((invoice) => [invoice.No, invoice.Pre_Assigned_No])
              .filter(Boolean)
          ),
        ];

    let allPaymentLines = [];

    if (historicos) {
      for (const no of invoiceIds) {
        // Buscar líneas de pago para esa factura
        const paymentLines = (
          await db.invoicePaymentLines.where("Document_No_").equals(no).toArray()
        ).filter(sinLasDeducidas);

        // Chequeo si existe en paymentLines, ya que divide por metodo de pago. En invoiceLines solo toma uno
        let hasPaymentLines = paymentLines.length > 0;
        let linesToUse = [];

        if (hasPaymentLines) {
          linesToUse = paymentLines;
        } else {
          // Factura sin líneas de pago → sumar monto total de invoiceLines
          const lines = await db.invoiceLines.filter((l) => l.invoiceNo === no).toArray();
          const totalFactura = sumMoney(lines, (l) => l.Line_Amount, decimals);

          linesToUse = [
            {
              invoiceNo: no,
              rawAmount: totalFactura,
            },
          ];
        }

        // Mapear y agregar al arreglo principal
        const mappedLines = linesToUse.map((line) => ({
          ...line,
          rawAmount: hasPaymentLines ? line.Amount : line.Line_Amount || 0,
          invoiceNo: no,
          Payment_Method: (line?.Payment_Method && line?.Payment_Method.trim()) || "EFECTIVO",
        }));

        allPaymentLines.push(...mappedLines);
      }
    } else {
      // Por las DOS claves, no sólo por invoiceId.
      //
      // Un pago creado en este terminal se enlaza por `invoiceId`; uno traído
      // por Traer Históricos no lo tiene --llega con `invoiceId: 0`-- y sólo
      // trae el número de documento. Buscar por una sola clave dejaba fuera a
      // la mitad, y el detalle de un cierre importado salía vacío aunque las
      // facturas estuvieran ahí.
      //
      // Y no es cuestión de elegir la rama según de dónde venga el cierre:
      // después de un Traer Históricos, **un mismo rango contiene documentos de
      // los dos orígenes**. Por eso se cruza por las dos y no por un indicador.
      const idsLocales = new Set(invoicesInRange.map((inv) => inv.id).filter((x) => x != null));
      const numerosDeDocumento = new Set(
        invoicesInRange.flatMap((inv) => [inv.No, inv.Pre_Assigned_No]).filter(Boolean)
      );
      // Solo los CONTABILIZADOS tienen `No`. Business Central rellena
      // `Documento_Definitivo` tambien en los borradores, y con un numero
      // inventado que no es el que tendran al contabilizar --medido:
      // PVMDSC3R-002285 frente a la serie real PVMDSC3R-000005xxx--. Si el
      // rango no tiene ningun documento contabilizado, ese campo no puede
      // decidir nada y filtrar por el se llevaba puestos todos los pagos.
      const registradosEnRango = new Set(invoicesInRange.map((inv) => inv.No).filter(Boolean));

      allPaymentLines = await db.invoicePaymentLines
        .filter((line) => {
          if (line.deleted) return false;
          if (!sinLasDeducidas(line)) return false;
          if (!idsLocales.has(line.invoiceId) && !numerosDeDocumento.has(line.Document_No_)) {
            return false;
          }
          // El número de borrador lo pueden compartir dos documentos
          // contabilizados: si el pago dice a cuál pertenece, tiene que ser uno
          // de los del rango. Sin contabilizados en el rango no hay nada contra
          // que comparar, y el campo no descarta.
          if (!registradosEnRango.size || !line.Documento_Definitivo) return true;
          return registradosEnRango.has(line.Documento_Definitivo);
        })
        .toArray();

      allPaymentLines = allPaymentLines.map((line) => {
        const relatedInvoice =
          invoicesInRange.find((inv) => inv.id === line.invoiceId) ||
          invoicesInRange.find(
            (inv) => inv.No === line.Document_No_ || inv.Pre_Assigned_No === line.Document_No_
          );
        return {
          ...line,
          rawAmount: line.Amount || 0,
          // El `No` de un documento sin contabilizar es cadena vacia, no nulo:
          // con un `||` a secas caia al invoiceId --que en lo importado es 0--
          // y la linea se quedaba sin numero, sin cliente y sin el signo de la
          // nota de credito.
          invoiceNo: relatedInvoice?.No || relatedInvoice?.Pre_Assigned_No || line.invoiceId,
          Payment_Method: line.Payment_Method || relatedInvoice?.Payment_Method || "EFECTIVO",
        };
      });
    }

    registro.log(`Líneas de pago encontradas: ${allPaymentLines.length}`);

    const formasDePago = await formaDePagoService.getAll();

    // Enriquecer y ajustar montos
    const enrichedPaymentLines = allPaymentLines.map((line) => {
      const relatedInvoice = resolveInvoiceForCierreLine(line, invoicesInRange);

      const formaPago = formasDePago.find(
        (fp) => fp.Code.trim().toUpperCase() === (line.Payment_Method || "").trim().toUpperCase()
      );

      // Ajuste de signo según tipo de documento
      const docSign = getDocumentTypeSign(relatedInvoice?.Document_Type);
      const adjustedAmount = roundMoney(
        docSign < 0 ? -Math.abs(line.rawAmount) : line.rawAmount,
        decimals
      );

      // Prioridad para identificar la factura: 1) No (BC), 2) Pre_Assigned_No, 3) ID interno de Dexie.
      // Se respeta en todos lados donde se muestra/mapea el número de documento (columna
      // "Factura" del cierre y Document_No enviado a Business Central).
      const displayDocNo = getFirstNonEmpty(
        relatedInvoice?.No,
        relatedInvoice?.Pre_Assigned_No,
        relatedInvoice?.id,
        line.invoiceId
      );

      // El número con que Business Central conoce la venta --`No` o
      // `Pre_Assigned_No`--, o null si nunca llegó.
      //
      // `invoiceNo` no sirve para saberlo: para poder pintar la columna cae al id
      // de Dexie o a «Desconocido», y ninguno de los dos existe en BC. Lo lee el
      // detalle del cierre, que sólo manda ventas con número. En Traer Históricos
      // el pago se buscó por número, así que ése vale aunque la factura no se
      // encuentre. Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H4 · TPV.
      const numeroDeBC = getFirstNonEmpty(
        relatedInvoice?.No,
        relatedInvoice?.Pre_Assigned_No,
        historicos ? line.invoiceNo : null
      );

      return {
        ...line,
        Amount: adjustedAmount,
        invoiceNo: displayDocNo ?? "Desconocido",
        numeroDeBC,
        clientName: relatedInvoice?.Bill_to_Name || "Cliente desconocido",
        documentType: relatedInvoice?.Document_Type || "Unknown",
        paymentMethodCode: formaPago?.Code || line.Payment_Method || "-",
        paymentMethodName: formaPago?.Description || "-",
        paymentMethodType: formaPago?.Tipo_Pago || "Efectivo",
      };
    });

    // Agrupar por factura y acumular montos
    const groupedByInvoice = Object.values(
      enrichedPaymentLines.reduce((acc, line) => {
        const key = `${line.invoiceNo}-${line.paymentMethodCode}`;
        if (!acc[key]) {
          acc[key] = {
            invoiceNo: line.invoiceNo,
            numeroDeBC: line.numeroDeBC,
            clientName: line.clientName,
            documentType: line.documentType,
            paymentMethodCode: line.paymentMethodCode,
            paymentMethodName: line.paymentMethodName,
            paymentMethodType: line.paymentMethodType,
            Amount: line.Amount, // iniciamos con el monto de la primera línea
            lines: [line], // y guardamos la primera línea
          };
        } else {
          // si ya existe, vamos acumulando
          acc[key].Amount = addMoney(acc[key].Amount, line.Amount, decimals);
          acc[key].lines.push(line);
        }
        return acc;
      }, {})
    );

    // Total general (solo para logging)
    const totalAmount = sumMoney(groupedByInvoice, (inv) => inv.Amount, decimals);
    registro.log(`Total de pagos en el rango: ${totalAmount.toFixed(2)}`);
    return groupedByInvoice;
  } catch (error) {
    registro.error(`Error al obtener líneas de pago para el rango de fechas:`, error);
    throw error;
  }
};

export const getPaymentLinesByNo = async (nos) => {
  const paymentLines = await db.invoicePaymentLines.where("Document_No_").anyOf(nos).toArray();

  return paymentLines;
};
